import React, { useEffect, useRef, useState } from "react";
import useVariousStudentRecords from "../../../../hooks/useVariousStudentRecords";
import styles from "../CustomPrint.module.css";

const SCHOOL_LOGO_URL =
  "https://res.cloudinary.com/dc9kbbxxe/image/upload/v1752733952/eduseed/shineschool/%EB%B9%9B%EC%9D%98%EC%9E%90%EB%85%80%ED%95%99%EA%B5%90%EB%A1%9C%EA%B3%A0_NEW__%EC%84%B1%EC%A0%81%ED%91%9C%EC%9A%A9_zaem4l.png";

function isBlank(value) {
  return value === undefined || value === null || value.toString() === "";
}

function getValue(valueObject, ...keys) {
  const foundKey = keys.find((key) => !isBlank(valueObject?.[key]));
  return foundKey ? valueObject[foundKey] : "";
}

function valueWithUnit(value, unit = "") {
  if (isBlank(value)) return "";
  if (!unit || /미검사|정상|이상|해당없음/.test(value.toString())) {
    return value;
  }
  return `${value}${unit}`;
}

function valueWithRank(value, rank, unit = "") {
  if (isBlank(value) && isBlank(rank)) return "";

  return (
    <>
      {valueWithUnit(value, unit)}
      {!isBlank(rank) && (
        <>
          <br />({rank}등급)
        </>
      )}
    </>
  );
}

export default function ShineschoolPhysicalReportForm({
  schoolId = "",
  student = null,
  filters = {},
  schoolYearLabel = "",
  onPrintStateChange = () => {},
}) {
  const [record, setRecord] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const requestKeyRef = useRef("");
  const { getVariousStudentRecordByUserId } = useVariousStudentRecords();
  const getVariousStudentRecordByUserIdRef = useRef(
    getVariousStudentRecordByUserId,
  );
  const valueObject = record?.value_object || {};
  const studentStage = student?.[1] || "";
  const studentGrade = student?.extra_student_year || student?.[2] || "";
  const studentName = student?.fullname || "";
  const bmi = getValue(valueObject, "bmi");
  const bmiClass = getValue(valueObject, "bmiClass");

  useEffect(() => {
    if (!student?.username || !filters?.schoolYearId) {
      setRecord(null);
      setIsLoading(false);
      onPrintStateChange({
        isLoading: false,
        canPrint: false,
        message: "",
      });
      return;
    }

    const requestKey = `${schoolId}-${student.username}-${filters.schoolYearId}`;
    requestKeyRef.current = requestKey;
    setRecord(null);
    setIsLoading(true);
    onPrintStateChange({
      isLoading: true,
      canPrint: false,
      message: "체력검사 기록을 불러오는 중입니다...",
    });

    getVariousStudentRecordByUserIdRef.current.mutate(
      {
        schoolid: schoolId,
        username: student.username,
        school_year_id: filters.schoolYearId,
        record_name: "physicalRecord",
      },
      {
        onSuccess: (data) => {
          if (requestKeyRef.current !== requestKey) return;

          const rows = Array.isArray(data) ? data : [];
          const selectedRecord =
            rows.find((row) => row.group?.toString() === "1") ||
            rows[0] ||
            null;

          setRecord(selectedRecord);
          setIsLoading(false);
          onPrintStateChange({
            isLoading: false,
            canPrint: !!selectedRecord,
            message: selectedRecord
              ? ""
              : "선택한 학년도 체력검사 기록이 없습니다.",
          });
        },
        onError: (error) => {
          if (requestKeyRef.current !== requestKey) return;

          console.error(error);
          setRecord(null);
          setIsLoading(false);
          onPrintStateChange({
            isLoading: false,
            canPrint: false,
            message: `체력검사 기록을 불러오지 못했습니다. ${error.message}`,
          });
        },
      },
    );
  }, [filters?.schoolYearId, onPrintStateChange, schoolId, student?.username]);

  if (!student?.username) {
    return (
      <div className={`no-print ${styles.emptyState}`}>학생을 선택하세요.</div>
    );
  }

  if (isLoading) {
    return (
      <div className={`no-print ${styles.emptyState}`}>
        체력검사 기록을 불러오는 중입니다...
      </div>
    );
  }

  if (!record) {
    return (
      <div className={`no-print ${styles.emptyState}`}>
        선택한 학년도 체력검사 기록이 없습니다.
      </div>
    );
  }

  return (
    <section className={styles.reportPage}>
      <div className={styles.topSpacer}></div>
      <h1 className={styles.reportTitle}>신체 및 체력검사 통지서</h1>

      <div className={styles.studentInfoLine}>
        {schoolYearLabel && <span>{schoolYearLabel}학년도 </span>}
        <span>{studentStage}</span>학교{" "}
        <span>{studentGrade && `${studentGrade}학년 `}</span>
        <span>{studentName}</span>
      </div>

      <table className={styles.physicalTable}>
        <colgroup>
          <col style={{ width: "12%" }} />
          <col style={{ width: "15%" }} />
          <col />
        </colgroup>
        <thead>
          <tr>
            <th
              style={{ padding: "12px", backgroundColor: "transparent" }}
              className={styles.backslash}
              colSpan={2}
            >
              <div>학년</div>
              구분
            </th>
            <th>{studentGrade ? `${studentGrade}학년` : "선택 학생"}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td rowSpan={5}>신체검사</td>
            <td>키</td>
            <td>{valueWithUnit(getValue(valueObject, "height"), "cm")}</td>
          </tr>
          <tr>
            <td>몸무게</td>
            <td>{valueWithUnit(getValue(valueObject, "weight"), "kg")}</td>
          </tr>
          <tr>
            <td>BMI</td>
            <td>
              {bmi}
              {!isBlank(bmiClass) && (
                <>
                  <br />({bmiClass})
                </>
              )}
            </td>
          </tr>
          <tr>
            <td>시력</td>
            <td>
              좌 {getValue(valueObject, "leftEye")}
              <br />우 {getValue(valueObject, "rightEye")}
            </td>
          </tr>
          <tr>
            <td>소변검사</td>
            <td>{getValue(valueObject, "urinalysis")}</td>
          </tr>
          <tr>
            <td rowSpan={7}>체력검사</td>
            <td>
              왕복
              <br />
              오래달리기
            </td>
            <td>
              {valueWithRank(
                getValue(valueObject, "longRunRepeat"),
                getValue(valueObject, "longRunRepeatRank"),
                "회",
              )}
            </td>
          </tr>
          <tr>
            <td>오래달리기</td>
            <td>
              {valueWithRank(
                getValue(valueObject, "longRun", "longrun"),
                getValue(valueObject, "longRunRank", "longrunRank"),
              )}
            </td>
          </tr>
          <tr>
            <td>유연성</td>
            <td>
              {valueWithRank(
                getValue(valueObject, "flexibility"),
                getValue(valueObject, "flexibilityRank"),
              )}
            </td>
          </tr>
          <tr>
            <td>팔굽혀펴기</td>
            <td>
              {valueWithRank(
                getValue(valueObject, "pushup", "pushUp"),
                getValue(valueObject, "pushupRank", "pushUpRank"),
                "회",
              )}
            </td>
          </tr>
          <tr>
            <td>
              윗몸
              <br />
              말아올리기
            </td>
            <td>
              {valueWithRank(
                getValue(valueObject, "crunch"),
                getValue(valueObject, "crunchRank"),
                "회",
              )}
            </td>
          </tr>
          <tr>
            <td>
              50m
              <br />
              달리기
            </td>
            <td>
              {valueWithRank(
                getValue(valueObject, "50mRun"),
                getValue(valueObject, "50mRunRank"),
              )}
            </td>
          </tr>
          <tr>
            <td>
              제자리
              <br />
              멀리뛰기
            </td>
            <td>
              {valueWithRank(
                getValue(valueObject, "jumpFar"),
                getValue(valueObject, "jumpFarRank"),
                "cm",
              )}
            </td>
          </tr>
        </tbody>
      </table>

      <div className={styles.schoolFooter}>
        <div className={styles.logoBox}>
          <img src={SCHOOL_LOGO_URL} alt="학교 로고" width="auto" height="65" />
        </div>
        {/* <div className={styles.schoolName}>
          <strong>빛의자녀학교</strong>
        </div> */}
      </div>
    </section>
  );
}
